import React from 'react'
import {
  Div,
  Flex,
  LoadingSpinner,
  Text,
} from '@sendoutcards/quantum-design-ui'
import { Icon } from 'src/design_system/atoms/icons/components/Icon'
import { IconType } from 'src/design_system/atoms/icons/types'
import { useSelector, useState } from 'src/hooks'
import { Dialog } from 'src/design_system/molecules/dialog/Dialog'
import styles from './membershipRewardsWidget.module.scss'
import { useRewardSummary } from 'src/react_query'
import { MotionCounter } from './MotionCounter'
import { membershipCoinsBase64 } from './MembershipCoinsBase64'
import { motion } from 'framer-motion'
import { clamp } from 'lodash'
import MembershipRewardsList from './MembershipRewardsList'
import { formatDate, getDaysUntil } from 'src/helpers/formatDate'

export type MappedReward = {
  count: number
  originalQty: number
  purchaseDate: Date
  expireDate: Date
}

type MembershipRewardsWidgetProps = {
  title?: string
  subtitle?: string
  amount?: number
  icon?: IconType
}

const ICON_SIZE = 52

const mapAmountToBgPosition = (amount: number): number => {
  const MIN_AMOUNT = 150
  const MAX_AMOUNT = 1400
  const MIN_Y = 200
  const MAX_Y = 120

  const clampedAmount = clamp(amount, MIN_AMOUNT, MAX_AMOUNT)

  const t = (clampedAmount - MIN_AMOUNT) / (MAX_AMOUNT - MIN_AMOUNT)

  return MIN_Y + t * (MAX_Y - MIN_Y)
}

export const isExpiringSoon = (reward: MappedReward) => {
  return getDaysUntil(reward.expireDate) < 31
}

export const MembershipRewardsWidget: React.FC<
  MembershipRewardsWidgetProps
> = ({
  title = 'Membership Rewards',
  subtitle = 'Rewards Balance',
  amount = 0,
  icon = 'reward_coin',
}) => {
  const windowWidth = useSelector(state => state.window.width)
  const isSmallMobile = windowWidth <= 420

  const { data, isFetching } = useRewardSummary()

  const oldestReward = data?.rewardHistory.edges.flatMap(page =>
    page?.node
      ? {
          count: page.node.count,
          originalQty: page.node.originalQty,
          purchaseDate: new Date(page.node.purchaseDate),
          expireDate: new Date(page.node.expires),
        }
      : null,
  )[0]

  const upcomingReward = data?.rewardUpcoming

  const [shouldShowDetailedBuckets, setShouldShowDetailedBuckets] =
    useState(false)

  const bgPosition = mapAmountToBgPosition(amount)

  return (
    <Flex
      flexDirection="column"
      alignItems="stretch"
      backgroundColor="foreground"
      borderRadius="32px"
      inset={{ vertical: 'x2', horizontal: 'x2_5' }}
      boxShadow="light"
      width="100%"
      rowGap="x1_5"
      height="fit-content"
    >
      <Flex width="100%" alignItems="center" columnGap="x1">
        <Icon name={icon} size={ICON_SIZE} />
        <Text
          type="body"
          weight="bold"
          color="primaryHeading"
          content={title}
        />
      </Flex>
      <Div
        style={{
          height: 1,
          width: '100%',
          backgroundColor: '#EEF0F2',
        }}
      />
      <Flex
        columnGap="x1"
        rowGap="x1"
        flexDirection={isSmallMobile ? 'column' : 'row'}
        minWidth="150px"
      >
        <motion.div
          className={styles.totalWrapper}
          initial={{ backgroundPosition: '100% 230%' }}
          animate={{ backgroundPosition: `100% ${bgPosition}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          style={{
            backgroundImage: `url(${membershipCoinsBase64})`,
          }}
        >
          <Text type="caption" color="primaryBody" content={subtitle} />
          <Flex flexGrow={1} alignItems="center" style={{ marginTop: '10px' }}>
            <div className={styles.totalAmount}>
              <MotionCounter amount={amount} />
              <Text type="caption" color="primaryBody" content="points" />
            </div>
          </Flex>
        </motion.div>
        {upcomingReward ? (
          <Flex
            columnGap="x1"
            rowGap="x1"
            flexDirection={isSmallMobile ? 'column' : 'row'}
            minWidth="150px"
          >
            <Flex
              style={{
                backgroundColor: '#f5f6f7',
                borderRadius: 16,
                padding: '8px 16px',
              }}
              flexDirection="column"
            >
              <Text
                type="caption"
                color="primaryBody"
                content="Upcoming Rewards:"
              />
              <div className={styles.upcomingContent}>
                <Text
                  type="caption"
                  color="primaryBody"
                  content={upcomingReward.count}
                  alignment="left"
                  style={{
                    fontSize: '2.25rem',
                    fontWeight: '700',
                    color: '#404040',
                    marginTop: '10px',
                  }}
                />
                <Text
                  type="caption"
                  color="primaryBody"
                  alignment="left"
                  content={`on ${formatDate(new Date(upcomingReward.purchaseDate), false)}`}
                  style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#404040',
                    marginTop: '4px',
                  }}
                />
              </div>
            </Flex>
          </Flex>
        ) : null}

        <Flex
          style={{
            backgroundColor: '#f5f6f7',
            borderRadius: 16,
            padding: '8px 16px 16px 16px',
            minWidth: '160px',
          }}
          flexDirection="column"
        >
          <Text type="caption" color="primaryBody" content="Next to Expire:" />
          {isFetching && !oldestReward ? <LoadingSpinner size="large" /> : null}
          {oldestReward ? (
            <>
              <Flex columnGap="x_5" flexWrap="wrap">
                <Text
                  type="caption"
                  color="primaryBody"
                  content={oldestReward.count.toLocaleString()}
                  style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: isExpiringSoon(oldestReward) ? 'red' : '#404040',
                  }}
                />
                <Text
                  type="caption"
                  color="primaryBody"
                  content={`on ${formatDate(oldestReward.expireDate, true)}`}
                  style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: isExpiringSoon(oldestReward) ? 'red' : '#404040',
                  }}
                />
              </Flex>
              <div
                style={{
                  borderRadius: '20px',
                  padding: '2px 12px',
                  marginTop: '8px',
                  backgroundColor: '#02c5e3',
                  cursor: 'pointer',
                }}
                onClick={() => setShouldShowDetailedBuckets(true)}
              >
                <Text
                  type="footnote"
                  content="View list"
                  weight="bold"
                  color="accent"
                  alignment="center"
                />
              </div>
            </>
          ) : null}
        </Flex>
      </Flex>
      {oldestReward ? (
        <Dialog
          isOpen={shouldShowDetailedBuckets}
          onClose={() => setShouldShowDetailedBuckets(false)}
          title="Detailed Membership Rewards Information"
          sheetBgColorOverride="#F8F5F6DA"
          titleBarBackgroundColor="#FFF"
        >
          <MembershipRewardsList />
        </Dialog>
      ) : null}
    </Flex>
  )
}

export default MembershipRewardsWidget
